import { Spacing } from '@persona-id/design-tokens';
import { FC, FocusEvent, MouseEvent, PropsWithChildren, useCallback, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled, { css } from 'styled-components';

import {
  DragHandleLocation,
  ItemTypes,
  LAYOUT_ROW_HEIGHT,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { MODULE_DATA_FOCUS_VALUE } from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import {
  moduleOutlineMixin,
  ModulePreview,
} from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/shared';
import NodeSticker from 'dashboard/components/NodeSticker';
import { BaseModule } from 'dashboard/lib/module';
import Card from 'design-system/components/Card';
import useToggle from 'lib/hooks/useToggle';

const DraggableGridModuleContainer = styled('div')<{
  isSelected: boolean;
  isHovering: boolean;
  showBounds: boolean;
}>`
  ${moduleOutlineMixin}
  position: absolute;
  padding: ${Spacing[2]};
  transition: transform 100ms ease;
  min-width: 0; // prevents grid blowout for elements that are larger than the minimum width
  height: 100%;

  ${({ isSelected, isHovering, showBounds }) => css`
    ${showBounds && 'outline-style: dashed;'}

    ${isHovering && 'outline-style: solid;'}
    ${isHovering && 'z-index: 1;'}

    ${isSelected && 'outline-style: solid;'}
    ${isSelected && 'z-index: 2;'}
  `}
`;

const TitleContainer = styled('div')`
  bottom: 100%;
  position: absolute;
  height: 25px;
`;

const StyledModuleCard = styled(Card)<{ disableScroll: boolean; hidden?: boolean }>`
  overflow-y: auto;
  height: 100%;

  ${({ disableScroll, hidden }) => css`
    pointer-events: ${disableScroll ? 'none' : 'auto'};
    display: ${hidden ? 'none' : 'block'};
  `}
`;

const HandleCSS = {
  [DragHandleLocation.TopLeft]: css`
    top: 0;
    left: 0;
    transform: translate(-50%, -50%);

    &:hover {
      cursor: nwse-resize;
    }
  `,
  [DragHandleLocation.TopRight]: css`
    top: 0;
    right: 0;
    transform: translate(50%, -50%);

    &:hover {
      cursor: nesw-resize;
    }
  `,
  [DragHandleLocation.BottomLeft]: css`
    bottom: 0;
    left: 0;
    transform: translate(-50%, 50%);

    &:hover {
      cursor: nesw-resize;
    }
  `,
  [DragHandleLocation.BottomRight]: css`
    bottom: 0;
    right: 0;
    transform: translate(50%, 50%);

    &:hover {
      cursor: nwse-resize;
    }
  `,
};

export const Handle = styled('div')<{ location: DragHandleLocation }>`
  height: 10px;
  width: 10px;
  position: absolute;
  border-radius: 5px;
  outline: 2px solid ${({ theme }) => theme.color.primary.brand600};
  background-color: white;
  z-index: 1;

  ${({ location }) => HandleCSS[location]}
`;

interface DraggableGridItemProps {
  module: BaseModule;
  onModuleSelect?: (moduleToken: string) => void;
  isSelected: boolean;
  isDragOverCanvas: boolean;
  columnWidth: number;
  title: string;
}

export const DraggableGridItem: FC<PropsWithChildren<DraggableGridItemProps>> = ({
  children,
  module,
  onModuleSelect,
  isSelected,
  isDragOverCanvas,
  columnWidth,
  title,
}) => {
  const [isHovering, { enable: setIsHovering, disable: setStopHovering }] = useToggle(false);
  const { position, token } = module;
  const isDragging = isSelected && isDragOverCanvas;
  const showLabel = isHovering || isSelected;

  const [_, dragRef, preview] = useDrag(
    () => ({
      type: ItemTypes.Module,
      item: { module },
    }),
    [module],
  );

  // disable default drag preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const handleModuleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onModuleSelect(token);
    },
    [token],
  );

  const handleModuleFocus = useCallback(
    (e: FocusEvent<HTMLDivElement, Element>) => {
      e.stopPropagation();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onModuleSelect(token);
    },
    [token],
  );

  const handleModuleDragStart = useCallback(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onModuleSelect(token);
  }, [token]);

  return (
    <DraggableGridModuleContainer
      data-test={`draggable-module-${module.token}`}
      data-focus={MODULE_DATA_FOCUS_VALUE}
      style={{
        transform: `translate(${position.x * columnWidth}px, ${position.y * LAYOUT_ROW_HEIGHT}px)`,
        width: `${position.width * columnWidth}px`,
        height: `${position.height * LAYOUT_ROW_HEIGHT}px`,
      }}
      ref={dragRef}
      showBounds={isDragOverCanvas}
      isSelected={isSelected}
      isHovering={isHovering}
      onMouseEnter={() => setIsHovering()}
      onMouseLeave={setStopHovering}
      onDragStart={handleModuleDragStart}
      onClick={handleModuleClick}
      onFocus={handleModuleFocus}
      tabIndex={0}
    >
      {showLabel && (
        <TitleContainer data-test={`module-label-${module.token}`}>
          <NodeSticker title={title} selected={isSelected} />
        </TitleContainer>
      )}
      {isSelected && (
        <>
          <DragHandles module={module} location={DragHandleLocation.TopLeft} />
          <DragHandles module={module} location={DragHandleLocation.TopRight} />
          <DragHandles module={module} location={DragHandleLocation.BottomLeft} />
          <DragHandles module={module} location={DragHandleLocation.BottomRight} />
        </>
      )}
      {/* We're using css to toggle element visibility over display logic so the elements and their children don't need to unmount */}
      <ModulePreview data-test='module-preview' hidden={!isDragging} />
      <StyledModuleCard disableScroll={isDragOverCanvas} hidden={isDragging}>
        {children}
      </StyledModuleCard>
    </DraggableGridModuleContainer>
  );
};

interface DrangHandleProps {
  module: BaseModule;
  location: DragHandleLocation;
}

export const DragHandles: FC<DrangHandleProps> = ({ module, location }) => {
  const [_, dragRef, preview] = useDrag(
    () => ({
      type: ItemTypes.ModuleDragHandle,
      item: { module, location },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [location, module],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return <Handle ref={dragRef} location={location} />;
};
